import { Alert, Paper, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { errorCopy } from '../../api/errors';
import { enumLabel } from '../../lib/enums';
import { formatMoney } from '../../lib/format';

export const REFERRAL_REPORT_PATH = '/admin/v1/reports/referrals';

/**
 * Who actually sent the business, and what it cost to have it sent.
 *
 * The referral screens answer two other questions well — what one partner is owed right now, and
 * the history of every commission ever accrued — and neither of them answers this one. A driver
 * argues from a RANKING over a stretch of time: "I brought you thirty tables in July." Nothing in
 * the office could produce that, so the conversation was had against a list of individual accruals
 * or against nothing at all, on the figure a payout is negotiated from.
 *
 * ## One row per partner PER CURRENCY, and no total across them
 *
 * A dollar table earns a dollar commission and a lempira table earns a lempira one — the commission
 * engine goes out of its way to keep the two apart, and this report was fixed once already for
 * adding them at one to one: a $10 commission plus an L 2,000 one came back as 2,010 of nothing, at
 * an exchange rate of about twenty-six to one. So the currency is on the row, every figure beside it
 * is in that money, and the columns are not addable down the page across currencies.
 *
 * ## Ranked by what they SENT, not by what they earned
 *
 * The two orders are different whenever partners are on different plans, and the question this
 * table exists for is which partners matter to the business. Commission is what that costs, and it
 * belongs beside the sales rather than in front of them — a partner on a flat rate can out-earn one
 * who sends twice the trade, and sorting on the payout would put him at the top of a table headed
 * with the wrong question. The server already sorts on referred sales; nothing here re-sorts it.
 */

interface LeaderboardRow {
  profileId: string;
  profileName: string;
  /** The money this row is in. Every figure beside it is denominated in this and only this. */
  currency: string;
  referredSalesMinor: number;
  commissionMinor: number;
}

interface ReferralReport {
  leaderboard: LeaderboardRow[];
  /** Everything never handed over, per currency, from the beginning — not what these dates earned. */
  unpaidLiability: { currency: string; amountMinor: number }[];
}

export function ReferralLeaderboard({ from, to }: { from: string; to: string }) {
  const report = useQuery({
    queryKey: ['referral-leaderboard', from, to],
    queryFn: () =>
      rawApi<ReferralReport>(
        'GET',
        `${REFERRAL_REPORT_PATH}?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      ),
    enabled: Boolean(from && to),
  });

  const failure = report.isError ? errorCopy(report.error, 'load the referral figures') : null;
  const owed = report.data?.unpaidLiability ?? [];

  return (
    <Paper withBorder p="md" radius="md" mb="md">
      <Title order={5}>Who sent the business</Title>
      <Text size="sm" c="dimmed" mt={2} mb="sm">
        Drivers, hotels and agencies, by what the tables they sent actually spent in these dates. Each row is
        in the money that trade was in — a dollar table earns a dollar commission, and the two are never added
        together.
      </Text>

      {failure ? (
        <Alert color="red" title={failure.title}>
          {failure.message}
        </Alert>
      ) : (
        <DataTable
          idAccessor={(r) => `${r.profileId}|${r.currency}`}
          records={report.data?.leaderboard ?? []}
          fetching={report.isFetching}
          noRecordsText="Nobody sent business in these dates"
          columns={[
            // The endpoint names the partner itself, so this is a name and not a lookup — and the
            // row is a way in to the partner's own record, which is where a payout is made.
            {
              accessor: 'profileName',
              title: 'Partner',
              render: (r) => (
                <Text size="sm" fw={500} component={Link} to={`/referrals/partners/${r.profileId}`}>
                  {r.profileName}
                </Text>
              ),
            },
            { accessor: 'currency', title: 'Money', render: (r) => enumLabel('currency', r.currency) },
            {
              accessor: 'referredSalesMinor',
              title: 'They sent',
              textAlign: 'right',
              render: (r) => formatMoney(r.referredSalesMinor, r.currency),
            },
            {
              accessor: 'commissionMinor',
              title: 'Commission earned',
              textAlign: 'right',
              render: (r) => formatMoney(r.commissionMinor, r.currency),
            },
          ]}
        />
      )}

      {/*
        The running balance, which the dates above do not touch — the same shape as "Tips still
        owed" further down this page and for the same reason: what is outstanding is not a figure
        about a range, and printing it inside one invites it to be read as one.

        A TOTAL, and only a total. Who is owed what, and whether they have reached their minimum,
        is on the partner screen and is not repeated here: a figure with two homes is a figure that
        eventually disagrees with itself.
      */}
      {owed.length > 0 && (
        <Text size="xs" c="dimmed" mt="sm">
          {owed.map((o) => formatMoney(o.amountMinor, o.currency)).join(' and ')} of commission has never been
          handed over, from the very beginning — the dates above do not change that.{' '}
          <Text span component={Link} to="/referrals?tab=balances" inherit>
            Who is owed it
          </Text>
          .
        </Text>
      )}
    </Paper>
  );
}
